/**
 * Locale-aware navigation hooks (issue #221 PR #2, Step 4).
 *
 * Every in-app link / programmatic navigation must stay within the ACTIVE
 * locale. Without prefixing, a click on `/categories/x` from `/en-us/...` would
 * be bookmark-migrated to the STORE DEFAULT locale — silently flipping the
 * user's language. These hooks read the active locale from the URL and prefix
 * targets through the pure `withLocale` helper.
 *
 *   useActiveLocale()      → the canonical active locale (from the URL).
 *   useLocalizedTo()       → (path) => locale-prefixed href for <a>/<Link>.
 *   useLocalizedNavigate() → navigate(), prefixing the active locale.
 */

import { useCallback } from "react";
import { useLocation, useNavigate, type NavigateOptions } from "react-router-dom";
import { readLocaleCatalog } from "../i18n/localeCatalog";
import { splitLocaleFromPath, withLocale } from "../i18n/localeRouting";

/** The canonical active locale derived from the URL (falls back to store default). */
export function useActiveLocale(): string {
	const { pathname } = useLocation();
	const { supported, defaultLocale } = readLocaleCatalog();
	return splitLocaleFromPath(pathname, supported).locale ?? defaultLocale;
}

/** Returns a builder that prefixes an absolute in-app path with the active locale. */
export function useLocalizedTo(): (path: string) => string {
	const locale = useActiveLocale();
	return useCallback((path: string) => withLocale(locale, path), [locale]);
}

/** A `navigate()` that prefixes absolute in-app targets with the active locale. */
export function useLocalizedNavigate(): (to: string, options?: NavigateOptions) => void {
	const navigate = useNavigate();
	const locale = useActiveLocale();
	return useCallback(
		(to: string, options?: NavigateOptions) => {
			navigate(withLocale(locale, to), options);
		},
		[navigate, locale],
	);
}
